/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Image as SharedImage,
  Heading,
  Text,
  headingType,
  tags,
  size,
  updateAEMCQKeys,
  RichText,
} from '@marriott/mi-ui-library';
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { FC, lazy, startTransition, Suspense, useState, useEffect } from 'react';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { PromoProps, PublishCardWrapperProps } from './Promo.types';
import playBtn from '../../../assets/images/lovetravels/play.svg';
import Image from 'next/image';

import { StyledComp } from './Promo.styles';
import clsx from 'clsx';

export const PromoComp: FC<PromoProps> = ({
  eyebrowtext,
  header,
  description,
  imageAlignment,
  linkUrl,
  linkText,
  headerTag = tags.h2,
  contentBlockAlignment,
  trackingProperties,
  styleclass,
  dynamicMedia,
  headerFontSize = size.small,
  descriptionCustomClass,
  headerCustomClass,
  custom_click_track_value = '',
  ...props
}) => {
  const renditions = dynamicMedia?.renditions;
  const textAlignClasses = contentBlockAlignment === 'center' ? 'text-center' : 'text-left';
  const customAttributes = trackingProperties?.enableScrollingBehavior
    ? { 'data-section-tracking': props?.componentId }
    : {};
  const isAuthorMode = AuthoringUtils.isInEditor();
  const updatedProps = updateAEMCQKeys(props);
  const cqPath = updatedProps['cqPath'];
  const parts = cqPath.split('/jcr:content');
  const pagePath = parts[0];
  const itemPath = parts[1];
  const isInEditor = updatedProps['isInEditor'];

  const cqItems = updatedProps['cqItems'];
  let featuredCard: any;

  if (cqItems && Object.prototype.hasOwnProperty.call(cqItems, 'featuredcardcontainer')) {
    const featuredCardContainer = cqItems['featuredcardcontainer'];
    featuredCard = featuredCardContainer?.[':items'];
  }

  const localModules: Record<string, () => Promise<any>> = {
    CardLayered: () => import('@marriott/mi-ui-library'),
    Media: () => import('@marriott/mi-ui-library'),
    MiniCardCarouselContainer: () => import('../MiniCardCarouselContainer'),
  };

  const mapper: Record<string, string> = {};
  mapper['featuredcard'] = 'CardLayered';
  mapper['media'] = 'Media';
  mapper['cardminicarouselcontainer'] = 'MiniCardCarouselContainer';

  const FallbackComponent: React.FC = () => null;

  const getComponent = (moduleName: string) => {
    return lazy(async () => {
      const module = await localModules[moduleName]();
      const Component = module[moduleName] || module.default;
      return { default: Component ?? FallbackComponent };
    });
  };

  const authorTextContainer = (name: string) => {
    return (
      <ResponsiveGrid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${name}`}
        columnCount="12"
        removeDefaultStyles={true}
        isInEditor={isInEditor}
      />
    );
  };

  const authorCardWrapper = (cardName: string) => {
    return (
      <ResponsiveGrid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${cardName}`}
        columnCount="12"
        gridClassNames={''}
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-martech/components/container',
        }}
        isInEditor={isInEditor}
      />
    );
  };

  const PublishCardWrapper: FC<PublishCardWrapperProps> = (props: PublishCardWrapperProps) => {
    const { cardName, jsonData } = props;
    const [Component, setComponent] = useState<React.LazyExoticComponent<any> | null>(null);
    const [componentProps, setComponentProps] = useState<any>(null);

    useEffect(() => {
      if (jsonData && Object.prototype.hasOwnProperty.call(jsonData, cardName)) {
        const card = jsonData[cardName];
        const cardItems = card[':items'];

        for (const itemKey in cardItems) {
          if (Object.prototype.hasOwnProperty.call(cardItems, itemKey)) {
            const item = cardItems[itemKey];
            const itemType = item[':type']?.split('/').pop();
            const items = item[':items'];

            if (Object.prototype.hasOwnProperty.call(mapper, itemType)) {
              const innerComp = mapper[itemType];

              // ✅ Defer component creation until after hydration
              startTransition(() => {
                setComponent(getComponent(innerComp));
                setComponentProps({
                  ...item,
                  cqItems: items,
                  additionalWrapperClass: 'lt-commit-item',
                  isAuthorMode,
                  featuredCard,
                });
              });

              return; // Exit loop after scheduling update
            }
          }
        }
      }
    }, [cardName, jsonData]);

    if (!Component || !componentProps) return null;

    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Component {...componentProps} />
      </Suspense>
    );
  };

  const hasVideo = Boolean(linkUrl) && 'video' == linkText;
  const imageSection = (
    <SharedImage
      customClass="lt-commit-featured-img"
      altText={dynamicMedia?.altText}
      renditions={renditions}
      dynamic={dynamicMedia?.dynamic}
      defaultImageURL={dynamicMedia?.assetPath}
    />
  );
  const computedClickTrackValue =
    custom_click_track_value ||
    (trackingProperties?.clickTrack
      ? `${trackingProperties?.trackingContentPosition || ''}|${trackingProperties?.trackingDescription || ''}|external`
      : undefined);

  const imageSectionWrapped = hasVideo ? (
    <a
      href={linkUrl}
      className={clsx('lt-image col-lg-7', custom_click_track_value ? 'custom_click_track' : '')}
      data-custom_click_track_value={custom_click_track_value}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Play video"
    >
      <div className="col-12 d-flex align-items-center lt-commit-image justify-content-">{imageSection}</div>
      <button className="play-button" aria-label="Play video">
        <Image src={playBtn} alt="play" width={50} height={50} />
      </button>
    </a>
  ) : (
    <div className="col-lg-7 d-flex align-items-center lt-commit-image">{imageSection}</div>
  );

  return (
    <StyledComp
      data-component-name="m-ui-library-promo"
      data-testid="ui-library-Promo"
      alignElement={contentBlockAlignment}
      imgAlignment={imageAlignment}
      className={clsx(`${styleclass ? styleclass : ''} lt-commit ${props?.colorClass ? props?.colorClass : ''}`)}
      {...customAttributes}
    >
      <div className="container p-sm-0">
        <div className="row d-flex align-items-lg-center lt-image-wrapper">
          {imageSectionWrapped}
          <div
            className={clsx('col-lg-5 d-flex justify-content-lg-start flex-column lt-commit-text', textAlignClasses)}
          >
            {eyebrowtext && <span className="lt-kicker-1">{eyebrowtext}</span>}
            <Heading
              customClass={clsx('lt-title-2', headerCustomClass)}
              dangerouslySet={header}
              variation={headingType.title}
              fontSize={headerFontSize}
              element={headerTag}
            />
            {description && (
              <Text
                customClass={clsx('feature-lt-text', descriptionCustomClass)}
                copyText={description}
                fontSize={size.small}
                element={tags.paragraph}
              />
            )}
          </div>
        </div>
        {!isAuthorMode && featuredCard && (
          <div className="row lt-commit-featured">
            <div className="col-12 col-lg-6 pr-lg-3">
              <PublishCardWrapper cardName={'featuredcardcontainer'} jsonData={cqItems} />
              <div className="lt-credit-line">
                <RichText {...cqItems.container?.[':items']?.richtext} />
              </div>
            </div>
            <div className="lt-links col-12 col-lg-6">
              <PublishCardWrapper cardName={'featuredItems'} jsonData={cqItems} />
            </div>
          </div>
        )}
        {!isAuthorMode && !featuredCard && (
          <div className="lt-links">
            <PublishCardWrapper cardName={'featuredItems'} jsonData={cqItems} />
          </div>
        )}
        {!isAuthorMode && <PublishCardWrapper cardName={'media'} jsonData={cqItems} />}
        {isAuthorMode && (
          <>
            <div className="lt-commit-featured">{authorCardWrapper('featuredcardcontainer')}</div>
            {authorTextContainer('container')}
            <div className="lt-auth-links">{authorCardWrapper('featuredItems')}</div>
          </>
        )}
      </div>
    </StyledComp>
  );
};

export const PromoConfig = {
  emptyLabel: 'LoveTravelsPromo',
  isEmpty: () => true,
};

export const LTPromo = (props: PromoProps) => (
  <div data-testid="lt-promo" data-component-name="o-prearrival-lovetravels-promo">
    <EditableComponent config={PromoConfig} {...props}>
      <PromoComp {...props} />
    </EditableComponent>
  </div>
);
